import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CollectionPreferences,
  Header,
  Pagination,
  PaginationProps,
  SpaceBetween,
  Table
} from '@amzn/awsui-components-react';

import EmptyTableBox from '../../common/EmptyTableBox/EmptyTableBox';
import InternalLink from '../../common/navigation/InternalLink';

import { getString } from '../../common/ui-string-labels/ui-strings-utils';
import { useGetOrdersUploadFiles } from '../../hooks/orders';
import { usePreferences } from '../../hooks/usePreferences';
import { buildCollectionPreferencesProps, buildColumnDefinitions } from '../../common/utils/table-utils';
import { ColumnSetting } from '../../common/utils/config-models';
import filesTableConfig from './upload-orders.table-config.json';
import { NonCancelableEventHandler } from '@amzn/awsui-components-react/polaris/internal/events';
import _ from 'lodash';
import UploadOrdersModal from 'components/UploadOrdersModal/UploadOrdersModal';
import { BulkUploadHistory } from '@amzn/ito-client';
import './UploadOrdersTable.scss';
import { FilesService } from 'services/files-service';

const COLUMN_SETTINGS = filesTableConfig.columns as ColumnSetting[];
const PREFS_CONFIG = buildCollectionPreferencesProps(COLUMN_SETTINGS);
const PREFERENCES_KEY = 'upload-orders-preferences';
const DEFAULT_PAGE_SIZE = 30;
const UPLOADED_STATUS = 'UPLOADED';
const PROCESSING_STATUS = 'PROCESSING';

const UploadOrdersTable = () => {
  const [visibleOrdersModal, setVisibleOrdersModal] = useState(false);

  const MILLISECONDS_BETWEEN_REFRESH_WHEN_PENDING_STATUS = 15000; // 15 seconds
  const MILLISECONDS_BETWEEN_REFRESH_WHEN_NO_PENDING_STATUS = 300000; // 5 minutes

  const { currentPreferences, onPreferencesConfirmHandler } = usePreferences({
    preferencesKey: PREFERENCES_KEY,
    defaultPaging: DEFAULT_PAGE_SIZE,
    columnsSettings: COLUMN_SETTINGS,
    refreshData: () => {}
  });
  const PAGE_SIZE: number = currentPreferences.pageSize ?? DEFAULT_PAGE_SIZE;

  const [currentPageIndex, setCurrentPageIndex] = useState<number>(1);
  const [filesData, isLoading, doGetFiles] = useGetOrdersUploadFiles({ pageSize: PAGE_SIZE, pageIndex: 1 }, undefined);

  const previousPageSize = useRef(PAGE_SIZE);
  useEffect(() => {
    if (previousPageSize.current === PAGE_SIZE) return;
    previousPageSize.current = PAGE_SIZE;
    setCurrentPageIndex(1);
    doGetFiles({ pageSize: PAGE_SIZE, pageIndex: 1 });
  }, [PAGE_SIZE]);

  const columnDefinitions = buildColumnDefinitions(COLUMN_SETTINGS, currentPreferences.visibleContent);

  const getRefreshInterval = (): number => {
    if (
      _.findIndex(
        filesData?.files,
        (i: BulkUploadHistory) => i.status === UPLOADED_STATUS || i.status === PROCESSING_STATUS
      ) !== -1
    ) {
      return MILLISECONDS_BETWEEN_REFRESH_WHEN_PENDING_STATUS;
    } else {
      return MILLISECONDS_BETWEEN_REFRESH_WHEN_NO_PENDING_STATUS;
    }
  };

  const dismissModal = () => {
    setVisibleOrdersModal(false);
    doGetFiles({ pageSize: PAGE_SIZE, pageIndex: currentPageIndex });
  };

  // Pagination
  const onPageClick: NonCancelableEventHandler<PaginationProps.ChangeDetail> = e => {
    doGetFiles({ pageSize: PAGE_SIZE, pageIndex: e.detail.currentPageIndex });
    setCurrentPageIndex(e.detail.currentPageIndex);
  };

  // Ready to display
  let pagesCount = 0;
  let totalResults = 0;
  if (filesData && filesData.totalResults && filesData.totalResults > 0) {
    totalResults = filesData.totalResults;
    pagesCount = Math.ceil(filesData.totalResults / PAGE_SIZE);
  }

  // Auto refresh
  useEffect(() => {
    const interval = setInterval(() => {
      doGetFiles({ pageSize: PAGE_SIZE, pageIndex: currentPageIndex, fromAutoRefresh: true });
    }, getRefreshInterval());

    return () => clearInterval(interval);
  }, [doGetFiles]);

  return (
    <Box data-testid="upload-orders-box">
      <UploadOrdersModal visible={visibleOrdersModal} onDismissModal={dismissModal} />
      <Table
        data-testid="upload-orders-table"
        header={
          <SpaceBetween size="m">
            <Header
              variant="awsui-h1-sticky"
              counter={`(${totalResults})`}
              description={getString('uploadOrders.table.headerDescription')}
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button data-testid="download-button" variant="normal" onClick={() => FilesService.instance.downloadOrdersUploadTemplate()}>
                    {getString("uploadOrders.table.downloadTemplateButton")}
                  </Button>                  
                  <Button data-testid="upload-button" variant="primary" onClick={() => setVisibleOrdersModal(true)}>
                    {getString('uploadOrders.table.uploadOrdersButton')}
                  </Button>
                </SpaceBetween>
              }
            >
              {getString('uploadOrders.table.headerTitle')}
            </Header>
          </SpaceBetween>
        }
        pagination={
          <Pagination
            pagesCount={pagesCount}
            currentPageIndex={currentPageIndex}
            onChange={onPageClick}
            ariaLabels={{
              nextPageLabel: getString('common.pagination.nextPage'),
              previousPageLabel: getString('common.pagination.nextPage'),
              pageLabel: (pageNumber: number) => getString('common.pagination.currentPage', { pageNumber, pagesCount })
            }}
          />
        }
        empty={<EmptyTableBox />}
        preferences={
          <CollectionPreferences
            title={getString('common.preferences.title')}
            confirmLabel={getString('common.preferences.confirmLabel')}
            cancelLabel={getString('common.preferences.cancelLabel')}
            preferences={currentPreferences}
            onConfirm={onPreferencesConfirmHandler}
            pageSizePreference={{
              title: getString('common.preferences.pageSize.title'),
              options: PREFS_CONFIG.pageSizeOptions!
            }}
            stripedRowsPreference={{
              label: getString('common.preferences.stripedRows.label'),
              description: getString('common.preferences.stripedRows.description')
            }}
            visibleContentPreference={{
              title: getString('common.preferences.visibleContentPreference.title'),
              options: [
                {
                  label: getString('common.preferences.visibleContentPreference.columns'),
                  options: PREFS_CONFIG.visibleContentOptions
                }
              ]
            }}
          />
        }
        columnDefinitions={columnDefinitions}
        stripedRows={currentPreferences.stripedRows}
        loading={isLoading}
        loadingText="Loading bulk uploads"
        items={filesData?.files!}
        variant="full-page"
      />
    </Box>
  );
};

export default UploadOrdersTable;
